import { APP_NAME } from '@/constants/brand';
import type { OrderDetail, OrderShippingInfo } from '@/types/order';
import {
  ORDER_STATUS_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
} from '@/types/order';
import { formatOrderMoney } from '@/utils/currency';
import { formatDateTime } from '@/utils/date';
import { downloadHtmlAsPdf } from '@/utils/downloadHtmlAsPdf';
import { openHtmlForPrint } from '@/utils/openHtmlForPrint';
import { ucfirst } from '@/utils/string';

export interface OrderInvoiceContext {
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
}

function labelFromOptions<T extends string>(
  options: ReadonlyArray<{ label: string; value: T }>,
  value: string,
): string {
  return options.find((option) => option.value === value)?.label ?? ucfirst(value);
}

function formatAddressLines(info: OrderShippingInfo, type: 'shipping' | 'billing'): string {
  const lines =
    type === 'shipping'
      ? [
          info.shippingName,
          info.shippingPhone,
          info.shippingAddress1,
          info.shippingAddress2,
          `${info.shippingCity}, ${info.shippingState} ${info.shippingPostalCode}`,
          info.shippingCountry,
        ]
      : [
          info.billingName,
          info.billingPhone,
          info.billingAddress1,
          info.billingAddress2,
          `${info.billingCity}, ${info.billingState} ${info.billingPostalCode}`,
          info.billingCountry,
        ];

  return lines.filter(Boolean).join('<br />');
}

function buildItemsTable(order: OrderDetail): string {
  if (order.items.length === 0) {
    return '<p class="muted">No items in this order.</p>';
  }

  const rows = order.items
    .map(
      (item) => `
      <tr>
        <td>
          <strong>${item.productName}</strong><br />
          <span class="muted">SKU: ${item.sku}</span>
          ${
            item.attributes.length
              ? `<br /><span class="muted">${item.attributes.map((a) => `${a.name}: ${a.value}`).join(' · ')}</span>`
              : ''
          }
        </td>
        <td class="num">${item.quantity}</td>
        <td class="num">${formatOrderMoney(item.salePrice, order.priceOrigin)}</td>
        <td class="num">${item.discount > 0 ? `-${formatOrderMoney(item.discount, order.priceOrigin)}` : '—'}</td>
        <td class="num">${formatOrderMoney(item.tax, order.priceOrigin)}</td>
        <td class="num"><strong>${formatOrderMoney(item.total, order.priceOrigin)}</strong></td>
      </tr>`,
    )
    .join('');

  return `
    <table>
      <thead>
        <tr>
          <th>Product</th>
          <th>Qty</th>
          <th>Price</th>
          <th>Discount</th>
          <th>Tax</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;
}

export function buildOrderInvoiceHtml(order: OrderDetail, context: OrderInvoiceContext): string {
  const details = order.details;
  const orderStatus = labelFromOptions(ORDER_STATUS_OPTIONS, order.orderStatus);
  const paymentStatus = labelFromOptions(PAYMENT_STATUS_OPTIONS, order.paymentStatus);
  const paymentMethod = labelFromOptions(PAYMENT_METHOD_OPTIONS, order.paymentMethod);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>Invoice — ${order.orderNumber}</title>
    <style>
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 32px;
        font-family: "Segoe UI", Arial, sans-serif;
        color: #0E0E13;
        background: #fff;
      }
      .header {
        display: flex;
        justify-content: space-between;
        gap: 24px;
        padding-bottom: 20px;
        border-bottom: 2px solid #115099;
        margin-bottom: 24px;
      }
      .brand {
        font-size: 24px;
        font-weight: 700;
        color: #115099;
        letter-spacing: 0.02em;
      }
      .invoice-title {
        margin: 4px 0 0;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: #5A5A62;
      }
      .meta {
        text-align: right;
        font-size: 13px;
        line-height: 1.6;
      }
      .meta strong {
        display: block;
        font-size: 18px;
        font-family: ui-monospace, monospace;
        color: #0E0E13;
      }
      .grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
        margin-bottom: 24px;
      }
      .panel {
        padding: 14px 16px;
        border: 1px solid #E8DFD4;
        border-radius: 10px;
        background: #FBFAF5;
      }
      .panel h3 {
        margin: 0 0 10px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #5A5A62;
      }
      .panel p {
        margin: 0;
        font-size: 13px;
        line-height: 1.55;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 24px;
        font-size: 13px;
      }
      th, td {
        padding: 10px 12px;
        border-bottom: 1px solid #E8DFD4;
        text-align: left;
        vertical-align: top;
      }
      th {
        background: #F7F5F0;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #5A5A62;
      }
      .num { text-align: right; white-space: nowrap; }
      .totals {
        margin-left: auto;
        width: min(100%, 320px);
      }
      .totals div {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        padding: 6px 0;
        font-size: 13px;
      }
      .totals .grand {
        margin-top: 8px;
        padding-top: 10px;
        border-top: 2px solid #115099;
        font-size: 16px;
        font-weight: 700;
      }
      .muted { color: #5A5A62; font-size: 12px; }
      .footer {
        margin-top: 28px;
        padding-top: 16px;
        border-top: 1px solid #E8DFD4;
        font-size: 12px;
        color: #5A5A62;
        text-align: center;
      }
      @media print {
        body { padding: 16px; }
      }
    </style>
  </head>
  <body>
    <div class="header">
      <div>
        <div class="brand">${APP_NAME}</div>
        <div class="invoice-title">Tax Invoice</div>
      </div>
      <div class="meta">
        <strong>${order.orderNumber}</strong>
        Placed: ${formatDateTime(order.placedAt)}<br />
        Status: ${orderStatus}<br />
        Payment: ${paymentMethod} · ${paymentStatus}
      </div>
    </div>

    <div class="grid">
      <div class="panel">
        <h3>Bill to</h3>
        <p>
          <strong>${context.customerName}</strong><br />
          ${context.customerPhone || '—'}<br />
          ${context.customerEmail || '—'}
        </p>
      </div>
      <div class="panel">
        <h3>Shipping address</h3>
        <p>${details ? formatAddressLines(details, 'shipping') : '—'}</p>
      </div>
      <div class="panel">
        <h3>Billing address</h3>
        <p>${details ? formatAddressLines(details, 'billing') : '—'}</p>
      </div>
      <div class="panel">
        <h3>Fulfillment</h3>
        <p>
          Courier: ${details?.courierName || '—'}<br />
          Tracking: ${details?.trackingNumber || '—'}<br />
          Coupon: ${order.couponCode || '—'}
        </p>
      </div>
    </div>

    ${buildItemsTable(order)}

    <div class="totals">
      <div><span>Subtotal</span><span>${formatOrderMoney(order.subtotal, order.priceOrigin)}</span></div>
      <div><span>Discount</span><span>-${formatOrderMoney(order.discountAmount, order.priceOrigin)}</span></div>
      <div><span>Shipping</span><span>${formatOrderMoney(order.shippingCharge, order.priceOrigin)}</span></div>
      <div><span>Tax</span><span>${formatOrderMoney(order.taxAmount, order.priceOrigin)}</span></div>
      <div class="grand"><span>Grand total</span><span>${formatOrderMoney(order.grandTotal, order.priceOrigin)}</span></div>
    </div>

    ${
      order.note?.trim()
        ? `<div class="panel" style="margin-top:16px"><h3>Order note</h3><p>${order.note}</p></div>`
        : ''
    }

    <div class="footer">
      Thank you for shopping with ${APP_NAME}. This is a computer-generated invoice.
    </div>
  </body>
</html>`;
}

export function printOrderInvoice(order: OrderDetail, context: OrderInvoiceContext): boolean {
  return openHtmlForPrint(buildOrderInvoiceHtml(order, context));
}

export async function downloadOrderInvoice(
  order: OrderDetail,
  context: OrderInvoiceContext,
): Promise<void> {
  await downloadHtmlAsPdf(
    buildOrderInvoiceHtml(order, context),
    `invoice-${order.orderNumber}.pdf`,
  );
}

export function buildOrderReceiptHtml(order: OrderDetail, context: OrderInvoiceContext): string {
  const details = order.details;
  const paymentStatus = labelFromOptions(PAYMENT_STATUS_OPTIONS, order.paymentStatus);
  const paymentMethod = labelFromOptions(PAYMENT_METHOD_OPTIONS, order.paymentMethod);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>Receipt — ${order.orderNumber}</title>
    <style>
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 32px;
        font-family: "Segoe UI", Arial, sans-serif;
        color: #0E0E13;
        background: #fff;
      }
      .header {
        display: flex;
        justify-content: space-between;
        gap: 24px;
        padding-bottom: 20px;
        border-bottom: 2px solid #115099;
        margin-bottom: 24px;
      }
      .brand {
        font-size: 24px;
        font-weight: 700;
        color: #115099;
        letter-spacing: 0.02em;
      }
      .invoice-title {
        margin: 4px 0 0;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: #5A5A62;
      }
      .stamp {
        display: inline-block;
        margin-top: 8px;
        padding: 4px 10px;
        border: 1px solid #115099;
        color: #115099;
        font-size: 11px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .meta {
        text-align: right;
        font-size: 13px;
        line-height: 1.6;
      }
      .meta strong {
        display: block;
        font-size: 18px;
        font-family: ui-monospace, monospace;
        color: #0E0E13;
      }
      .grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
        margin-bottom: 24px;
      }
      .panel {
        padding: 14px 16px;
        border: 1px solid #E8DFD4;
        border-radius: 10px;
        background: #FBFAF5;
      }
      .panel h3 {
        margin: 0 0 10px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #5A5A62;
      }
      .panel p {
        margin: 0;
        font-size: 13px;
        line-height: 1.55;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 24px;
        font-size: 13px;
      }
      th, td {
        padding: 10px 12px;
        border-bottom: 1px solid #E8DFD4;
        text-align: left;
        vertical-align: top;
      }
      th {
        background: #F7F5F0;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #5A5A62;
      }
      .num { text-align: right; white-space: nowrap; }
      .totals {
        margin-left: auto;
        width: min(100%, 320px);
      }
      .totals div {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        padding: 6px 0;
        font-size: 13px;
      }
      .totals .grand {
        margin-top: 8px;
        padding-top: 10px;
        border-top: 2px solid #115099;
        font-size: 16px;
        font-weight: 700;
      }
      .muted { color: #5A5A62; font-size: 12px; }
      .footer {
        margin-top: 28px;
        padding-top: 16px;
        border-top: 1px solid #E8DFD4;
        font-size: 12px;
        color: #5A5A62;
        text-align: center;
      }
      @media print {
        body { padding: 16px; }
      }
    </style>
  </head>
  <body>
    <div class="header">
      <div>
        <div class="brand">${APP_NAME}</div>
        <div class="invoice-title">Payment Receipt</div>
        <div class="stamp">${paymentStatus}</div>
      </div>
      <div class="meta">
        <strong>${order.orderNumber}</strong>
        Receipt date: ${formatDateTime(order.placedAt)}<br />
        Method: ${paymentMethod}
      </div>
    </div>

    <div class="grid">
      <div class="panel">
        <h3>Received from</h3>
        <p>
          <strong>${context.customerName}</strong><br />
          ${context.customerPhone || '—'}<br />
          ${context.customerEmail || '—'}
        </p>
      </div>
      <div class="panel">
        <h3>Payment summary</h3>
        <p>
          Amount: <strong>${formatOrderMoney(order.grandTotal, order.priceOrigin)}</strong><br />
          Status: ${paymentStatus}<br />
          Shipping: ${details ? formatAddressLines(details, 'shipping') : '—'}
        </p>
      </div>
    </div>

    ${buildItemsTable(order)}

    <div class="totals">
      <div><span>Subtotal</span><span>${formatOrderMoney(order.subtotal, order.priceOrigin)}</span></div>
      <div><span>Discount</span><span>-${formatOrderMoney(order.discountAmount, order.priceOrigin)}</span></div>
      <div><span>Shipping</span><span>${formatOrderMoney(order.shippingCharge, order.priceOrigin)}</span></div>
      <div><span>Tax</span><span>${formatOrderMoney(order.taxAmount, order.priceOrigin)}</span></div>
      <div class="grand"><span>Amount received</span><span>${formatOrderMoney(order.grandTotal, order.priceOrigin)}</span></div>
    </div>

    <div class="footer">
      This receipt acknowledges payment for order ${order.orderNumber}.
      Generated by ${APP_NAME}.
    </div>
  </body>
</html>`;
}

export function printOrderReceipt(order: OrderDetail, context: OrderInvoiceContext): boolean {
  return openHtmlForPrint(buildOrderReceiptHtml(order, context));
}

export async function downloadOrderReceipt(
  order: OrderDetail,
  context: OrderInvoiceContext,
): Promise<void> {
  await downloadHtmlAsPdf(
    buildOrderReceiptHtml(order, context),
    `receipt-${order.orderNumber}.pdf`,
  );
}
